import React, { useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useLazyQuery, useQuery } from '@apollo/react-hooks';
import SEO from 'react-seo-component';
import { JSONLD, GenericNode } from '@sekmet/react-structured-data';
import { useInView } from 'react-intersection-observer';

import './desktop-product-detail-style.scss';
import { CredentialContext, credentialType } from '@providers';
import useIsComDomain from '@src/hooks/useIsComDomain';

// image
import MgsIcon from '@src/imagesNew/logo/new-mgs-logo.png';
import SimplLogo from '@src/imagesNew/logo/simpl-logo.png';

// Components
import { NoResult } from '@molecules';
import DesktopProductDetailPageSkeleton from '@src/componentsNew/Skeleton/desktop/page/DesktopProductDetailPageSkeleton';
import Button from '@src/componentsNew/Buttons/Button';
import Typography from '@src/componentsNew/Typography/Typography';
import PincodeInput from '@src/componentsNew/Inputs/PincodeInput/PincodeInput';
import Carousel from '@src/componentsNew/Carousel/Carousel';
import ProductDetailCategory from '@src/componentsNew/ProductDetailCategory/ProductDetailCategory';
import DesktopProductDetailRecommendList from '@src/componentsNew/ProductList/ProductDetailRecommend/DesktopProductDetailRecommendList';
import DetailProductThumbnail from '@src/componentsNew/ProductThumbnail/DetailProductThumbnail/DetailProductThumbnail';
import ProductDetailDescription from '@src/componentsNew/ProductDetailDescription/ProductDetailDescription';
import DesktopProductDetailReview from '@src/componentsNew/ProductDetailReview/DesktopProductDetailReview';
import DesktopProductDetailReviewPreview from '@src/componentsNew/ProductDetailReview/DesktopProductDetailReviewPreview';
import DesktopDetailAddToCartButton from '@src/componentsNew/Buttons/AddToCartButton//DesktopDetailAddToCartButton';
import GlobalStoreTooltip from '@src/componentsNew/Tooltip/GlobalStoreTooltip';

// Icons
import ArrowForwardIcon from '@src/componentsNew/Icons/Icons/ArrowForwardIcon';
import WishListButton from '@src/componentsNew/Buttons/WishListButton/WishListButton';
import ShareIcon from '@src/componentsNew/Icons/Icons/ShareIcon';
import StarRatingIcon from '@src/componentsNew/Icons/StarRatingIcon';
import InfoIcon from '@src/componentsNew/Icons/Icons/InfoIcon';
import ExpandMoreIcon from '@src/componentsNew/Icons/Icons/ExpandMoreIcon';
import DoneRingIcon from '@src/componentsNew/Icons/Icons/DoneRingIcon';
import PackageIcon from '@src/componentsNew/Icons/Icons/PackageIcon';

// Modal & Tooltip
import ProductShareModal from '@src/componentsNew/Modals/ProductShareModal/ProductShareModal';
import FreebieEventModal from '@src/componentsNew/Modals/FreebieEventModal/FreebieEventModal';
import AvailableVoucherModal from '@src/componentsNew/Modals/AvailableVoucherModal/AvailableVoucherModal';
import SimplModal from '@src/componentsNew/Modals/SimplModal/SimplModal';
import MgsTooltip from '@src/componentsNew/Tooltip/MgsTooltip';

import { ME_FOR_NOTIFY_ME } from '@molecules/NotifyMeModal/queries';
import { meForNotifyMe } from '@molecules/NotifyMeModal/__generated__/meForNotifyMe';
import { PRODUCT, RecommendType } from '@components/ProductPage/ProductDetailPage/queries';
import {
  product,
  product_product_variants,
  product_product_productReviewsByUser,
} from '@components/ProductPage/ProductDetailPage/__generated__/product';
import { PRODUCT_SHARE } from '@src/components/ProductPage/ProductDetailSummary/queries';

import {
  gaV4GetItemsFromSingleProduct,
  gaV4ViewItem,
  gaV4ShareProduct,
  getGraphqlIdFromDBId,
  getDBIdFromGraphqlId, getHighestDiscountVoucher
} from "@src/core/utils";

import { pixelContentView } from '@src/utils/pixel';

import {
  getPriceInfoFromProduct,
  getPriceInfoFromVariant,
  formatPrice,
  caculateSimplPrice,
  caculateBeautyDeviceSimplPrice,
  addRecentlyViewedProducts,
  removeRecentlyViewedProduct,
} from '@src/utils';
import { shouldShowElementAccordingToDomain, goToGlobalStore } from '@src/utils/domainCheck';
import {
  BEAUTY_DEVICE_PRODUCT_IDS,
  HIDE_VOUCHER_BRAND_IDS,
  getInvisibleProductIds
} from '@src/utils/constants';
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useInvisibleProductIds } from "@src/hooks/useInvisibleProductIds";

const DesktopProductDetailPage = ({ match }) => {
  const ready = useInvisibleProductIds();

  const history = useHistory();
  const isComDomain = useIsComDomain();

  const { currentUser, setCurrentUser } = useContext<credentialType>(CredentialContext);
  const productId = getGraphqlIdFromDBId(match.params.id, 'Product');

  const [priceInfoObj, setPriceInfoObj] = useState({
    originalPrice: 0,
    finalPrice: 0,
    discountPercent: 0,
    isOnSale: false,
  });

  const { originalPrice, finalPrice, discountPercent, isOnSale } = priceInfoObj;

  // variant 선택 전 보여지는 이미지
  const [defaultBigImages, setDefaultBigImages] = useState([]);
  // const [defaultSmallImages, setDefaultSmallImages] = useState([]);

  // variant 선택 시 보여지는 이미지
  const [bigImages, setBigImages] = useState([]);
  // const [smallImages, setSmallImages] = useState([]);

  const [showVariants, setShowVariants] = useState<boolean>(false);
  const [selectedVariant, setSelectedVariant] = useState<product_product_variants | null>(null);
  const [recentReview, setRecentReview] = useState<product_product_productReviewsByUser | null>(null);

  // modal
  const [isMgsTooltipOpen, setIsMgsTooltipOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isFreebieModalOpen, setIsFreebieModalOpen] = useState(false);
  const [isVoucherModalOpen, setIsVoucherModalOpen] = useState(false);
  const [isSimplModalOpen, setIsSimplModalOpen] = useState(false);
  const [isGlobalStoreTooltipOpen, setIsGlobalStoreTooltipOpen] = useState(false);

  // pincode
  const [isOpenPincodeInputArea, setIsOpenPincodeInputArea] = useState<boolean>(false);
  const [pincode, setPincode] = useState('');

  const [meForNotifyMe] = useLazyQuery<meForNotifyMe>(ME_FOR_NOTIFY_ME, {
    fetchPolicy: 'no-cache',
    onCompleted: (data) => {
      const mobileNumber = data?.me?.mobileNumber;
      const mobileVerified = data?.me?.mobileVerified;

      const copyCurrentUser = JSON.parse(JSON.stringify(currentUser));
      copyCurrentUser.user.mobileNumber = Number(mobileNumber);
      copyCurrentUser.user.mobileVerified = mobileVerified;

      // local user info update
      setCurrentUser(copyCurrentUser);
    },
  });

  const [shareProduct] = useLazyQuery(PRODUCT_SHARE);

  const { data, loading, refetch } = useQuery<product>(PRODUCT, {
    variables: { id: productId },
    onCompleted: (data) => {
      const product = data?.product;

      if (product) {
        const items = gaV4GetItemsFromSingleProduct(product);
        gaV4ViewItem(items, product?.price?.amount ?? 0);
        pixelContentView(product?.name, product?.category?.name, product?.variants[0]?.sku, 'product', 'INR');

        setPriceInfoObj(getPriceInfoFromProduct(product));
        setShowVariants(product?.productType?.hasVariants && 1 < product?.variants?.length);

        if (product?.productReviewsByUser?.length > 0) {
          const sortedReviewByUpdatedAt = product?.productReviewsByUser?.sort(
            (a, b) => new Date(b?.updatedAt).getTime() - new Date(a?.updatedAt).getTime(),
          );

          setRecentReview(sortedReviewByUpdatedAt[0]);
        }

        function extractNotVariantImages(product) {
          let variantImageIds = new Set();

          for (const variant of product.variants) {
            for (const bigImage of variant.bigImages) {
              variantImageIds.add(bigImage.id);
            }
          }

          const notVariantBigImages = product.bigImages.filter((bigImage) => !variantImageIds.has(bigImage.id));
          const notVariantSmallImages = product.smallImages.filter((smallImage) => !variantImageIds.has(smallImage.id));

          return { notVariantBigImages, notVariantSmallImages };
        }

        const { notVariantBigImages, notVariantSmallImages } = extractNotVariantImages(product);

        setDefaultBigImages(notVariantBigImages);
        // setDefaultSmallImages(notVariantSmallImages);
        addRecentlyViewedProducts(Number(match.params.id), product.thumbnail.url, product.urlForGraphql);
      } else {
        removeRecentlyViewedProduct(Number(match.params.id));
      }
    },
  });

  // in View state
  const { ref: reviewRef, inView: reviewInView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  useEffect(() => {
    if (currentUser?.token) {
      meForNotifyMe();
    }
  }, []);

  useEffect(() => {
    if (selectedVariant) {
      setBigImages(selectedVariant?.bigImages);
      // setSmallImages(selectedVariant?.smallImages);
      setPriceInfoObj(getPriceInfoFromVariant(selectedVariant));
    } else {
      setPriceInfoObj(getPriceInfoFromProduct(data?.product));
    }
  }, [selectedVariant]);

  const handleClickVariant = (variant: product_product_variants) => {
    if (variant.id === selectedVariant?.id) {
      setSelectedVariant(null);
    } else {
      setSelectedVariant(variant);
    }
  };

  // Modal Open & Close
  const handleShareModalOpen = () => {
    setIsShareModalOpen(true);

    // 공유 시 이벤트
    const itemId = getDBIdFromGraphqlId(data?.product?.id)?.toString();
    shareProduct({ variables: { id: data?.product?.id } });
    gaV4ShareProduct(itemId);
  };

  const handleShareModalClose = () => {
    setIsShareModalOpen(false);
  };

  const handleFreebieModalOpen = () => {
    setIsFreebieModalOpen(true);
  };

  const handleFreebieModalClose = () => {
    setIsFreebieModalOpen(false);
  };

  const handleVoucherModalOpen = () => {
    setIsVoucherModalOpen(true);
  };

  const handleVoucherModalClose = () => {
    setIsVoucherModalOpen(false);
  };

  const handleSimplModalOpen = () => {
    setIsSimplModalOpen(true);
  };

  const handleSimplModalClose = () => {
    setIsSimplModalOpen(false);
  };

  const handleMgsTooltipOpen = () => {
    setIsMgsTooltipOpen(true);
  };

  const handleMgsTooltipClose = () => {
    setIsMgsTooltipOpen(false);
  };

  const handleGlobalStoreTooltipOpen = () => {
    setIsGlobalStoreTooltipOpen(true);
  };

  const handleGlobalStoreTooltipClose = () => {
    setIsGlobalStoreTooltipOpen(false);
  };

  const getPriceValidUntilDate = () => {
    let date = new Date();
    date.setDate(date.getDate() + 3);
    return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDay();
  };

  const goTotalReviewSection = () => {
    const totalReviewSection = document.getElementById('desktop-product-detail-review-area');

    if (totalReviewSection) {
      const elementPosition = totalReviewSection.getBoundingClientRect().top;
      const offsetPosition = elementPosition - 65;

      window.scrollBy({
        top: offsetPosition,
        behavior: 'smooth',
      });
    }
  };

  const isVisibleProduct = data?.product && !getInvisibleProductIds().includes(match.params.id);

  if (loading || !ready) {
    return <DesktopProductDetailPageSkeleton />;
  }

  return isVisibleProduct ? (
    <div id="desktop-product-detail-container">
      <SEO
        title={data?.product?.seoTitle || ''}
        titleTemplate={sessionStorage.getItem('shopName') || ''}
        titleSeparator={`-`}
        description={data?.product?.seoDescription || ''}
        image={data?.product?.seoImages[0]?.url || ''}
        pathname={data?.product?.urlForGraphql || ''}
        siteLanguage="en"
        siteLocale="en_GB"
        twitterUsername=""
      />
      <JSONLD>
        <GenericNode
          type="product"
          jsonldtype="Product"
          productID={getDBIdFromGraphqlId(data?.product.id).toString()}
          name={data?.product.name}
          image={data?.product.bigImages.map((image) => image.url)}
          description={data?.product.seoDescription}
          sku={data?.product.variants[0].sku}
        >
          <GenericNode type="brand" jsonldtype="thing" name={data?.product.brand} />
          <GenericNode
            type="offers"
            jsonldtype="Offer"
            itemCondition="http://schema.org/NewCondition"
            url={`https://maccaron.in${data?.product.urlForGraphql}`}
            priceCurrency="INR"
            price={data?.product?.availability.priceRange.start.gross.amount.toString()}
            availability="http://schema.org/InStock"
            priceValidUntil={getPriceValidUntilDate()}
          />
          {data?.product.productReviewsAggregation && (
            <GenericNode
              type="aggregateRating"
              jsonldtype="AggregateRating"
              ratingValue={data?.product.productReviewsAggregation.rating}
              reviewCount={data?.product.productReviewsAggregation.reviewCount}
            />
          )}
        </GenericNode>
      </JSONLD>
      <div className="desktop-product-detail-content-wrapper">
        {/* 상품 상세 상단 */}
        <div className="top-product-info-wrapper">
          <ProductDetailCategory category={data?.product.category} />

          <div className="image-summary-wrapper">
            {/* 상품 이미지 캐러셀 */}
            <div className="product-image-area">
              <Carousel
                customSetting={{
                  dots: true,
                  infinite: true,
                  arrows: false,
                  centerMode: true,
                  centerPadding: '0',
                  autoplay: true,
                  autoplaySpeed: 4000,
                  speed: 2000,
                  slidesToShow: 1,
                  slidesToScroll: 1,
                  className: 'custom-slider',
                  dotsClass: 'custom-dots',
                }}
              >
                {selectedVariant
                  ? bigImages.map((bigImage) => (
                      <img
                        key={bigImage.id}
                        className="desktop-product-carousel-img"
                        src={bigImage.url}
                        alt={bigImage?.alt ?? ''}
                      />
                    ))
                  : defaultBigImages.map((defaultBigImage) => (
                      <img
                        key={defaultBigImage.id}
                        className="desktop-product-carousel-img"
                        src={defaultBigImage.url}
                        alt={defaultBigImage?.alt ?? ''}
                      />
                    ))}
              </Carousel>
            </div>

            {/* 상품 요약 정보 */}
            <div className="product-detail-summary-area">
              <div className="brand-info">
                <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                  {data?.product.brand}
                </Typography>

                <Button
                  cName="brand-name-btn"
                  size="medium"
                  endIcon={<ArrowForwardIcon fillColor="#666666" />}
                  onClick={() => history.push(data?.product?.brandLink?.url)}
                >
                  <Typography variant="subtitle2" component="span" style={{ color: '#666666' }}>
                    Brand Home
                  </Typography>
                </Button>
              </div>

              <div className="name-price-info">
                <div className="product-name-btn-area">
                  <Typography cName="product-name" variant="subtitle1" component="span" style={{ color: '#090A0FDE' }}>
                    {data?.product.name}
                  </Typography>
                  <div className="icon-btn-wrapper">
                    {/* Domain Revert Comment */}
                    {/* {
                        shouldShowElementAccordingToDomain(isComDomain, data?.product.productType.isMgs) ?
                          <WishListButton
                            productPrice={data?.product?.availability?.priceRange?.start?.gross?.amount ?? 0}
                            previousPage="product_detail"
                            previousPath={history.location.pathname}
                            productName={data?.product.name}
                            productId={data?.product.id}
                            isSelected={data?.product.isOnWishlist} />
                          : null
                      } */}
                    <WishListButton
                      productPrice={data?.product?.availability?.priceRange?.start?.gross?.amount ?? 0}
                      previousPage="product_detail"
                      previousPath={history.location.pathname}
                      productName={data?.product.name}
                      productId={data?.product.id}
                      isSelected={data?.product.isOnWishlist}
                    />
                    <ShareIcon onClick={handleShareModalOpen} />
                  </div>
                </div>

                {data?.product?.productReviewsAggregation?.reviewCount > 0 ? (
                  <div className="review-area" onClick={goTotalReviewSection}>
                    <div className="rating-icon">
                      <StarRatingIcon />
                      <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                        {data?.product?.productReviewsAggregation?.rating.toFixed(1)}
                      </Typography>
                    </div>

                    <Typography variant="body3" component="span" style={{ color: '#212121' }}>
                      {`${data?.product?.productReviewsAggregation?.reviewCount} Reviews`}
                    </Typography>
                  </div>
                ) : null}

                <div
                  className={`price-type-wrapper ${
                    data?.product?.productReviewsAggregation?.reviewCount > 0 ? '' : 'pt'
                  }`}
                >
                  <div className="price-area">
                    <Typography variant="h7" component="span" style={{ color: '#212121' }}>
                      {formatPrice(finalPrice)}
                    </Typography>

                    {isOnSale && (
                      <>
                        <Typography variant="h7" component="span" style={{ color: '#FF3399' }}>
                          {`${discountPercent}%`}
                        </Typography>
                        <Typography variant="strikethrough2" component="span" style={{ color: '#666666' }}>
                          MRP : {formatPrice(originalPrice)}
                        </Typography>
                      </>
                    )}

                    {!data?.product.productType.isCodRestricted ? (
                      <Typography cName="cod-chip" variant="caption" component="span" style={{ color: '#666666' }}>
                        COD available
                      </Typography>
                    ) : null}
                  </div>
                </div>
                <Typography variant="caption" component="span" style={{ color: '#666666' }}>
                  Inclusive of all taxes
                </Typography>
                <div
                  className={`price-type-wrapper ${
                    data?.product?.productReviewsAggregation?.reviewCount > 0 ? '' : 'pt'
                  }`} style={{marginTop: '0.5rem'}}
                >
                  {data?.product.freebieEvents.length || data?.product.availableVouchers.length ? (
                    <div className="btn-area">
                      {data?.product.freebieEvents.length ? (
                        <Button cName="freebie-btn" size="small" onClick={handleFreebieModalOpen}>
                          Freebie
                        </Button>
                      ) : null}
                      {data?.product.availableVouchers.length &&
                      !HIDE_VOUCHER_BRAND_IDS.includes(
                        getDBIdFromGraphqlId(data?.product?.brandLink?.id, 'Brand').toString(),
                      ) ? (
                        <Button cName="voucher-btn" variant="contained" size="small" onClick={handleVoucherModalOpen} endIcon={<ChevronRightIcon/>}>
                          Get for {getHighestDiscountVoucher(data.product)} with a Voucher
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="delivery-variant-info">
                <div className={`simpl-wrapper ${showVariants ? 'pd' : ''}`}>
                  <div className="simpl-info-row">
                    <div className="simpl-text-wrapper">
                      {BEAUTY_DEVICE_PRODUCT_IDS.includes(match.params.id) ? (
                        <Typography variant="subtitle2" component="span" style={{ color: '#666666' }}>
                          upto 24 months EMI available
                        </Typography>
                      ) : (
                        <>
                          <Typography variant="subtitle2" component="span" style={{ color: '#666666' }}>
                            3 interest-free Payment by
                          </Typography>
                          <div className="simpl-modal-trigger" onClick={handleSimplModalOpen}>
                            <img className="simpl-logo" src={SimplLogo} width={56} />
                            <InfoIcon />
                          </div>
                        </>
                      )}
                    </div>

                    <Typography variant="subtitle2" component="span">
                      {BEAUTY_DEVICE_PRODUCT_IDS.includes(match.params.id)
                        ? `(${formatPrice(caculateBeautyDeviceSimplPrice(finalPrice))} + interest) / month`
                        : formatPrice(caculateSimplPrice(finalPrice))}
                    </Typography>
                  </div>

                  {BEAUTY_DEVICE_PRODUCT_IDS.includes(match.params.id) ? (
                    <Typography variant="body3" component="span" style={{ color: '#666666' }}>
                      * interest will be differ by bank
                    </Typography>
                  ) : null}
                </div>

                <div className={`type-info-row ${showVariants ? 'pd' : ''}`}>
                  {data?.product.productType.isMgs && (
                    <div className="mgs-type">
                      <div className="mgs-tooltip-wrapper">
                        <Typography variant="subtitle2" component="span" style={{ color: '#666666' }}>
                          Delivery Type
                        </Typography>

                        <MgsTooltip
                          open={isMgsTooltipOpen}
                          onOpen={handleMgsTooltipOpen}
                          onClose={handleMgsTooltipClose}
                        />
                      </div>

                      <img className="mgs-logo" src={MgsIcon} width={56} />
                    </div>
                  )}
                  <div className="pincode-trigger">
                    <Typography variant="subtitle2" component="span" style={{ color: '#666666' }}>
                      Delivery Availability
                    </Typography>
                    <ExpandMoreIcon
                      cName={`expand-icon ${isOpenPincodeInputArea ? 'less' : 'more'}`}
                      onClick={() => setIsOpenPincodeInputArea((prev) => !prev)}
                    />
                  </div>
                  {isOpenPincodeInputArea ? (
                    <PincodeInput productType={data?.product.productType} pincode={pincode} setPincode={setPincode} />
                  ) : null}
                </div>

                {showVariants && (
                  <div className="variants-select-area">
                    {selectedVariant ? (
                      <>
                        {selectedVariant?.isEndOfLife && selectedVariant?.stockQuantity <= 0 && (
                          <Typography variant="subtitle2" component="span" style={{ color: '#9E9E9E' }}>
                            {`${selectedVariant.name} | Discontinued`}
                          </Typography>
                        )}
                        {!selectedVariant?.isEndOfLife && selectedVariant?.stockQuantity <= 0 && (
                          <Typography variant="subtitle2" component="span" style={{ color: '#9E9E9E' }}>
                            {`${selectedVariant.name} | Out of stock`}
                          </Typography>
                        )}
                        {selectedVariant?.stockQuantity <= 5 && selectedVariant?.stockQuantity > 0 && (
                          <div>
                            <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                              {`${selectedVariant.name} | `}
                            </Typography>
                            <Typography variant="body3" component="span" style={{ color: '#FF3D00' }}>
                              {`${selectedVariant.stockQuantity} left only!`}
                            </Typography>
                          </div>
                        )}
                        {selectedVariant?.stockQuantity > 5 && (
                          <Typography variant="subtitle2" component="span" style={{ color: '#212121' }}>
                            {selectedVariant.name}
                          </Typography>
                        )}
                      </>
                    ) : (
                      <Typography
                        cName="variant-text"
                        variant="subtitle2"
                        component="span"
                        style={{ color: '#212121' }}
                      >
                        Please Select Variants
                      </Typography>
                    )}

                    <ul className="variant-image-wrapper">
                      {data?.product?.variants?.map((variant) => (
                        <li
                          key={variant.id}
                          className={`${variant.id === selectedVariant?.id ? 'selected' : ''}`}
                          onClick={() => handleClickVariant(variant)}
                        >
                          <img
                            src={variant?.attributes[0]?.value?.image?.url}
                            className={variant.stockQuantity <= 0 ? 'overlay' : ''}
                          />
                          {variant.stockQuantity <= 0 ? <div className="img-out-of-stock-line" /> : null}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="text-icon-info">
                <div className="icon-wrapper">
                  <DoneRingIcon />
                  <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                    100% Authentic Guaranteed
                  </Typography>
                </div>
                <Typography variant="body3" component="span" style={{ color: '#9E9E9E' }}>
                  |
                </Typography>
                <div className="icon-wrapper">
                  <PackageIcon />
                  <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
                    Free Shipping Above ₹700
                  </Typography>
                </div>
              </div>

              <div className="add-to-cart-button-area">
                {/* Domain Revert Comment */}
                {/* {
                    shouldShowElementAccordingToDomain(isComDomain, data?.product.productType.isMgs) ? (
                      <DesktopDetailAddToCartButton
                        isThumbnail={false}
                        product={data?.product}
                        selectedVariant={selectedVariant}
                        refetch={refetch}
                        showVariants={showVariants}
                      />
                    ) :
                      (
                        !isComDomain ?
                          <div className="go-global-store-btn-wrapper">
                            <Button
                              data-text="Go to Global Store"
                              cName="go-global-store-btn"
                              variant="outlined"
                              size="large"
                              onClick={(e) => goToGlobalStore(e, false, data?.product?.urlForGraphql)} />

                            <GlobalStoreTooltip
                              open={isGlobalStoreTooltipOpen}
                              onOpen={handleGlobalStoreTooltipOpen}
                              onClose={handleGlobalStoreTooltipClose}
                              isDetail={true}
                            />
                          </div> : null
                      )
                  } */}

                <DesktopDetailAddToCartButton
                  isThumbnail={false}
                  product={data?.product}
                  selectedVariant={selectedVariant}
                  refetch={refetch}
                  showVariants={showVariants}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Brand Recommend List */}
        <DesktopProductDetailRecommendList
          title={`[${data?.product.brand}] Most Popular Products`}
          productId={productId}
          recommendType={RecommendType.Brand}
          brandUrl={data?.product?.brandLink?.url}
        />

        {/* Review Preview Area */}
        {data?.product?.productReviewsAggregation?.reviewCount >= 4 ? (
          <DesktopProductDetailReviewPreview productId={productId} />
        ) : null}

        <div className="product-thumbnail-sticky-area">
          <div className="description-review-wrapper">
            {/* Description Area */}
            <ProductDetailDescription
              descriptionImages={data?.product.descriptionImages}
              descriptions={data?.product.descriptions}
              countryOfOrigin={data?.product.countryOfOrigin}
              seller={data?.product.seller}
              extraInformation={data?.product.extraInformation}
            />

            {/* Review Area */}
            <DesktopProductDetailReview
              ref={reviewRef}
              productId={productId}
              productRefetch={refetch}
              variants={data?.product.variants}
              showVariants={showVariants}
              recentReview={recentReview}
              productTotalReviewCount={data?.product.productReviewsAggregation?.reviewCount}
              isUserBlockedProductReview={data?.product.isUserBlockedProductReview}
            />
          </div>

          {/* Product Thumbnail */}
          <DetailProductThumbnail
            product={data?.product}
            selectedVariant={selectedVariant}
            refetch={refetch}
            showVariants={showVariants}
          />
        </div>

        {/*  Recommend List */}
        {reviewInView && (
          <>
            <DesktopProductDetailRecommendList
              title="You May Like"
              productId={productId}
              recommendType={RecommendType.Order}
            />

            <DesktopProductDetailRecommendList
              title="Others Bought Together"
              productId={productId}
              recommendType={RecommendType.User}
            />
          </>
        )}
      </div>

      {/* Modal */}
      {isShareModalOpen && (
        <ProductShareModal product={data?.product} open={isShareModalOpen} onClose={handleShareModalClose} />
      )}

      {isFreebieModalOpen && (
        <FreebieEventModal product={data?.product} open={isFreebieModalOpen} onClose={handleFreebieModalClose} />
      )}

      {isVoucherModalOpen && (
        <AvailableVoucherModal
          product={data?.product}
          selectedVariant={selectedVariant}
          open={isVoucherModalOpen}
          onClose={handleVoucherModalClose}
        />
      )}

      {isSimplModalOpen && <SimplModal open={isSimplModalOpen} onClose={handleSimplModalClose} />}
    </div>
  ) : (
    <div className="no-result-wrapper">
      <NoResult />
    </div>
  );
};

export default DesktopProductDetailPage;
